// ─── SocketContext — Socket.io para Chat (/chat) y Notificaciones (/notif) ──
// /chat  → M08 Chat por proyecto/tarea
// /notif → M12 Notificaciones en tiempo real

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import type { MensajeChat, ReaccionChat } from '@/types/chats';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';
const TOKEN_KEY = 'erp_token';

// ─── Payload de notificación WS (G07) ────────────────────────
export interface NotificacionWS {
  id: number;
  tipo: string;
  modulo: string;
  mensaje: string;
  creado_en: string;
  referencia_id: number | null;
}

interface SocketContextValue {
  // M08 Chat
  joinCanal:              (canalId: number) => void;
  leaveCanal:             (canalId: number) => void;
  // BUG-4276 · avisa cuando el chat volvió tras un corte: lo recibido mientras tanto hay que pedirlo de nuevo.
  onReconectado:          (handler: () => void) => () => void;
  enviarEscribiendo:      (canalId: number, escribiendo: boolean) => void;
  onMensaje:              (handler: (msg: MensajeChat) => void) => () => void;
  onEscribiendo:          (handler: (data: { usuario_id: number; nombre: string; escribiendo: boolean }) => void) => () => void;
  onMensajeEditado:       (handler: (data: { mensaje_id: number; mensaje_nuevo: string; editado_en: string }) => void) => () => void;
  onMensajeEliminado:     (handler: (data: { mensaje_id: number }) => void) => () => void;
  // El backend (chats.routes.ts) emite el resumen bajo la clave `reacciones_resumen`.
  onReaccionActualizada:  (handler: (data: { mensaje_id: number; reacciones_resumen: ReaccionChat[] }) => void) => () => void;

  // El backend emite `canal_archivado` a la sala del canal (chats.routes.ts, PATCH /chats/canales/:id/archivo).
  onCanalArchivado:       (handler: (data: { canal_id: number; archivado: boolean }) => void) => () => void;

  // M08 Tareas — actualizaciones en tiempo real
  onTareaActualizada:     (handler: () => void) => () => void;

  // M12 Notificaciones
  onNotificacion:         (handler: (notif: NotificacionWS) => void) => () => void;
  onBadgeCount:           (handler: (data: { no_leidas: number }) => void) => () => void;
}

const SocketCtx = createContext<SocketContextValue | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode; token: string | null }> = ({ children, token }) => {
  // BUG-4270 · el socket vive en estado (no en ref): los on* cambian de identidad cuando nace, y las
  // pantallas que montaron en el mismo render que el Provider re-enganchan su escucha.
  const [chatSocket,  setChatSocket]  = useState<Socket | null>(null);
  const [notifSocket, setNotifSocket] = useState<Socket | null>(null);
  // BUG-4276 · canales a los que la pantalla pidió entrar: al reconectar el servidor ya no nos tiene en sus salas.
  const canalesAbiertos = useRef(new Set<number>());
  const alReconectar = useRef(new Set<() => void>());

  // ── /chat namespace ───────────────────────────────────────
  useEffect(() => {
    if (!token) return;

    const socket = io(`${WS_URL}/chat`, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    // Al volver la red se pide entrar otra vez a cada canal abierto; el servidor valida igual que la primera vez (F-15).
    let yaConecto = false;
    socket.on('connect', () => {
      if (yaConecto) {
        canalesAbiertos.current.forEach((id) => socket.emit('join_canal', id));
        alReconectar.current.forEach((h) => h());
      }
      yaConecto = true;
    });
    socket.on('join_denegado', () => {
      toast.error('No pudimos abrir ese canal de chat. Es posible que ya no tengas acceso.', { id: 'chat-join-denegado' });
    });
    // El Manager se comparte entre namespaces: el escucha se quita al salir para no acumularse entre sesiones.
    const alRendirse = () => {
      toast.error('Se perdió la conexión con el chat. Recarga la página para volver a recibir mensajes.', { id: 'chat-sin-conexion', duration: 10000 });
    };
    socket.io.on('reconnect_failed', alRendirse);

    setChatSocket(socket);

    return () => {
      socket.io.off('reconnect_failed', alRendirse);
      socket.disconnect();
      canalesAbiertos.current.clear();
      setChatSocket(null);
    };
  }, [token]);

  // ── /notif namespace ──────────────────────────────────────
  useEffect(() => {
    if (!token) return;

    const socket = io(`${WS_URL}/notif`, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    setNotifSocket(socket);

    return () => {
      socket.disconnect();
      setNotifSocket(null);
    };
  }, [token]);

  // ── Chat helpers ──────────────────────────────────────────
  const joinCanal = useCallback((canalId: number) => {
    canalesAbiertos.current.add(canalId);
    chatSocket?.emit('join_canal', canalId);
  }, [chatSocket]);

  const leaveCanal = useCallback((canalId: number) => {
    canalesAbiertos.current.delete(canalId);
    chatSocket?.emit('leave_canal', canalId);
  }, [chatSocket]);

  const onReconectado = useCallback((handler: () => void) => {
    alReconectar.current.add(handler);
    return () => { alReconectar.current.delete(handler); };
  }, []);

  const enviarEscribiendo = useCallback((canalId: number, escribiendo: boolean) => {
    chatSocket?.emit('escribiendo', { canal_id: canalId, escribiendo });
  }, [chatSocket]);

  const onMensaje = useCallback((handler: (msg: MensajeChat) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('mensaje_nuevo', handler);
    return () => { sock.off('mensaje_nuevo', handler); };
  }, [chatSocket]);

  const onEscribiendo = useCallback((handler: (data: { usuario_id: number; nombre: string; escribiendo: boolean }) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('escribiendo', handler);
    return () => { sock.off('escribiendo', handler); };
  }, [chatSocket]);

  const onMensajeEditado = useCallback((handler: (data: { mensaje_id: number; mensaje_nuevo: string; editado_en: string }) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('mensaje_editado', handler);
    return () => { sock.off('mensaje_editado', handler); };
  }, [chatSocket]);

  const onMensajeEliminado = useCallback((handler: (data: { mensaje_id: number }) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('mensaje_eliminado', handler);
    return () => { sock.off('mensaje_eliminado', handler); };
  }, [chatSocket]);

  const onReaccionActualizada = useCallback((handler: (data: { mensaje_id: number; reacciones_resumen: ReaccionChat[] }) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('reaccion_actualizada', handler);
    return () => { sock.off('reaccion_actualizada', handler); };
  }, [chatSocket]);

  const onCanalArchivado = useCallback((handler: (data: { canal_id: number; archivado: boolean }) => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('canal_archivado', handler);
    return () => { sock.off('canal_archivado', handler); };
  }, [chatSocket]);

  const onTareaActualizada = useCallback((handler: () => void) => {
    const sock = chatSocket;
    if (!sock) return () => {};
    sock.on('tarea_creada', handler);
    sock.on('tarea_actualizada', handler);
    sock.on('tarea_estado_cambiado', handler);
    sock.on('comentario_nuevo', handler);
    return () => {
      sock.off('tarea_creada', handler);
      sock.off('tarea_actualizada', handler);
      sock.off('tarea_estado_cambiado', handler);
      sock.off('comentario_nuevo', handler);
    };
  }, [chatSocket]);

  // ── Notif helpers ─────────────────────────────────────────
  const onNotificacion = useCallback((handler: (notif: NotificacionWS) => void) => {
    const sock = notifSocket;
    if (!sock) return () => {};
    sock.on('nueva_notificacion', handler);
    return () => { sock.off('nueva_notificacion', handler); };
  }, [notifSocket]);

  const onBadgeCount = useCallback((handler: (data: { no_leidas: number }) => void) => {
    const sock = notifSocket;
    if (!sock) return () => {};
    sock.on('badge_count', handler);
    const alConectar = (data: { usuario_id: number; no_leidas: number }) => {
      handler({ no_leidas: data.no_leidas });
    };
    sock.on('connected', alConectar);
    return () => {
      sock.off('badge_count', handler);
      sock.off('connected', alConectar);
    };
  }, [notifSocket]);

  return (
    <SocketCtx.Provider value={{
      joinCanal, leaveCanal, onReconectado, enviarEscribiendo, onMensaje, onEscribiendo,
      onMensajeEditado, onMensajeEliminado, onReaccionActualizada,
      onCanalArchivado, onTareaActualizada,
      onNotificacion, onBadgeCount,
    }}>
      {children}
    </SocketCtx.Provider>
  );
};

export const useSocket = () => {
  const ctx = useContext(SocketCtx);
  if (!ctx) throw new Error('useSocket must be used inside SocketProvider');
  return ctx;
};

export { TOKEN_KEY };
